
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Plan, BillingPeriod } from '@/types/plans';
import { toast } from 'sonner';
import { PLAN_PRICING, PLAN_COMMERCIAL_NAMES, getPlanPrice, centsToReais } from '@/constants/plans';
import { ENABLE_ASAAS } from '@/constants/asaas-pricing';
import { useAsaasCheckout } from '@/hooks/useAsaasCheckout';
import { debugLogger } from '@/utils/debugLogger';

export const useSponsoredPlans = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Query para obter dados baseado no provider
  const { data: subscriptionData } = useQuery({
    queryKey: [ENABLE_ASAAS ? 'asaasHistory' : 'stripeHistory', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const functionName = ENABLE_ASAAS ? 'get-asaas-history' : 'get-stripe-history';
      const { data, error } = await supabase.functions.invoke(functionName);
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id
  });

  // Get pricing based on provider
  const getPrice = (planType: string, period: BillingPeriod) => {
    const role = 'sponsored';
    return centsToReais(getPlanPrice(planType as any, period, role));
  };

  // Definir os planos para patrocinados com nomes comerciais
  const plans: Plan[] = [
    {
      id: 'sponsored_free',
      name: PLAN_COMMERCIAL_NAMES.sponsored_free,
      description: 'Acesso completo com recursos premium',
      prices: { 
        weekly: 0,
        monthly: getPrice('sponsored_free', 'monthly'), 
        quarterly: getPrice('sponsored_free', 'quarterly') 
      },
      benefits: [
        'Visualizações ilimitadas',
        'Curtidas ilimitadas',
        'Conversas ilimitadas',
        'Filtros avançados',
        'Ver visitantes do perfil',
        'Suporte dedicado'
      ],
      icon: '💎',
      highlight: 'Sempre gratuito'
    },
    {
      id: 'sponsored_premium',
      name: PLAN_COMMERCIAL_NAMES.sponsored_premium,
      description: 'Destaque-se com recursos exclusivos',
      prices: { 
        weekly: 0,
        monthly: getPrice('sponsored_premium', 'monthly'), 
        quarterly: getPrice('sponsored_premium', 'quarterly') 
      },
      benefits: [
        'Todos os recursos Intro',
        'Destaque permanente no feed',
        'Filtro de verificados',
        '4 boosts/mês (12/trimestre)',
        'Comprar boosts adicionais',
        'Acesso prioritário',
        'Suporte prioritário 24/7'
      ],
      isPopular: true,
      badge: 'Destaque Total',
      icon: '👑',
      highlight: 'Para se destacar ainda mais'
    }
  ];

  // Query para obter o plano atual do usuário
  const { data: currentPlan = 'sponsored_free' } = useQuery({
    queryKey: ['currentPlan', user?.id],
    queryFn: async () => {
      if (!user?.id) return 'sponsored_free';

      const { data, error } = await supabase
        .from('profiles')
        .select('plan_type')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      return data?.plan_type || 'sponsored_free';
    },
    enabled: !!user?.id
  });

  // Get checkout hooks based on payment provider
  const { createCheckoutSession: createAsaasCheckout, isLoading: asaasLoading, paymentModal } = useAsaasCheckout();

  // Mutation para fazer upgrade do plano baseado no provider
  const upgradeMutation = useMutation({
    mutationFn: async ({ planId, billingPeriod }: { planId: string; billingPeriod: BillingPeriod }) => {
      if (!user?.id) throw new Error('User not authenticated');

      const selectedPlan = plans.find(p => p.id === planId);
      if (!selectedPlan) throw new Error('Plan not found');

      debugLogger.info('plans', `Starting ${ENABLE_ASAAS ? 'Asaas' : 'Stripe'} upgrade...`, { planId, billingPeriod });

      if (ENABLE_ASAAS) {
        // Para planos gratuitos, fazer upgrade direto com Asaas
        if (selectedPlan.prices[billingPeriod] === 0) {
          debugLogger.info('plans', 'Free plan upgrade, using Asaas sync approach...');
          
          const { data, error } = await supabase.functions.invoke('sync-asaas-data');
          if (error) throw error;
          
          return { immediate: true, redirect_url: null };
        }

        // Para planos pagos, usar create-asaas-checkout
        await createAsaasCheckout(planId, billingPeriod);
        return { success: true, provider: 'asaas' };
      } else {
        // Para planos gratuitos, fazer upgrade direto sem Stripe
        if (selectedPlan.prices[billingPeriod] === 0) {
          debugLogger.info('plans', 'Free plan upgrade, using sync approach...');
          
          // Chamar sync-stripe-data para garantir que estamos atualizados
          await supabase.functions.invoke('sync-stripe-data');
          
          return { immediate: true, redirect_url: null };
        }

        // Para planos pagos, usar create-checkout-session (Stripe-First)
        const { data, error } = await supabase.functions.invoke('create-checkout-session', {
          body: {
            planId,
            billingPeriod
          }
        });

        if (error) {
          debugLogger.error('plans', 'Error creating checkout session:', error);
          throw error;
        }

        debugLogger.info('plans', 'Checkout session created:', data);

        // Redirecionar para Stripe Checkout
        if (data.url) {
          window.open(data.url, '_blank');
        }

        return data;
      }
    },
    onSuccess: (data, variables) => {
      const planName = plans.find(p => p.id === variables.planId)?.name;
      
      if (ENABLE_ASAAS) {
        if (data?.immediate) {
          toast.success(`Plano ${planName} ativado com sucesso!`);
          // Invalidar queries relacionadas
          queryClient.invalidateQueries({ queryKey: ['currentPlan'] });
          queryClient.invalidateQueries({ queryKey: ['asaasHistory'] });
        } else if (data?.success) {
          toast.success(`Redirecionando para checkout do plano ${planName}...`);
        }
      } else {
        if (data?.immediate) {
          toast.success(`Plano ${planName} ativado com sucesso!`);
          // Invalidar queries relacionadas
          queryClient.invalidateQueries({ queryKey: ['currentPlan'] });
          queryClient.invalidateQueries({ queryKey: ['stripeHistory'] });
        } else if (data?.url) {
          toast.success(`Redirecionando para checkout do plano ${planName}...`);
        } else {
          toast.success(`Iniciando upgrade para o plano ${planName}...`);
        }
      }
    },
    onError: (error: any) => {
      debugLogger.error('plans', 'Error upgrading plan:', error);
      toast.error(error.message || 'Erro ao alterar plano. Tente novamente.');
    }
  });

  // Função para calcular economias com validações robustas
  const calculateSavings = (prices: any) => {
    // Validações de entrada
    if (!prices || typeof prices !== 'object') {
      console.warn('calculateSavings: preços inválidos', prices);
      return {
        monthly: { percentage: 0, amount: 0 },
        quarterly: { percentage: 0, amount: 0 }
      };
    }

    // Garantir que todos os preços sejam números válidos
    const weeklyPrice = Number(prices.weekly) || 0;
    const monthlyPrice = Number(prices.monthly) || 0;
    const quarterlyPrice = Number(prices.quarterly) || 0;

    // Verificar se os preços são válidos
    if (weeklyPrice <= 0 || isNaN(weeklyPrice)) {
      return {
        monthly: { percentage: 0, amount: 0 },
        quarterly: { percentage: 0, amount: 0 }
      };
    }

    const weeklyTotal = weeklyPrice * 4;
    const quarterlyWeeklyTotal = weeklyPrice * 12;

    // Calcular percentuais com validação
    const monthlyPercentage = weeklyTotal > 0 && monthlyPrice >= 0 
      ? Math.round(((weeklyTotal - monthlyPrice) / weeklyTotal) * 100) 
      : 0;

    const quarterlyPercentage = quarterlyWeeklyTotal > 0 && quarterlyPrice >= 0 
      ? Math.round(((quarterlyWeeklyTotal - quarterlyPrice) / quarterlyWeeklyTotal) * 100) 
      : 0;

    return {
      monthly: {
        percentage: Math.max(0, isNaN(monthlyPercentage) ? 0 : monthlyPercentage),
        amount: Math.max(0, weeklyTotal - monthlyPrice)
      },
      quarterly: {
        percentage: Math.max(0, isNaN(quarterlyPercentage) ? 0 : quarterlyPercentage),
        amount: Math.max(0, quarterlyWeeklyTotal - quarterlyPrice)
      }
    };
  };

  return {
    plans,
    currentPlan,
    upgradePlan: upgradeMutation.mutate,
    isLoading: upgradeMutation.isPending || (ENABLE_ASAAS && asaasLoading),
    calculateSavings,
    stripeSubscriptionPeriod: subscriptionData?.currentPlanInfo?.billing_period || null,
    paymentModal: ENABLE_ASAAS ? paymentModal : null
  };
};
